import { Box, Button, CircularProgress, Divider, Typography } from "@mui/material"
import { Dialog } from "../Dialog"
import { useTheme } from "@emotion/react"
import { FormProvider, useForm } from "react-hook-form"
import { Input } from "../Form/Input"
import { useContract } from "~/contexts"
import { useCallback, useEffect } from "react"
import { DealershipLoginModel, useUserContractUpdateDealershipLoginMutation } from "~/graphql/types"
import { toast } from "react-toastify"

type DealershipLoginModalProps = {
  isVisible: boolean
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>
}

export const DealershipLoginModal: React.FC<DealershipLoginModalProps> = ({ isVisible, setIsVisible }) => {
  const { selectedContract, setSelectedContract } = useContract()
  const theme = useTheme()
  const formMethods = useForm<DealershipLoginModel>()
  const [updateDealershipLogin, { loading: updateDealershipLoginIsLoading }] = useUserContractUpdateDealershipLoginMutation({
    onError(error) {
      toast.error(error.message)
    },
    onCompleted(data) {
      toast.success('Os dados de concessionária foram salvos!')
      setSelectedContract((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          dealershipLogin: data.userContractUpdateDealershipLogin.dealershipLogin
        }
      })
      setIsVisible(false)
    }
  })

  const onSubmit = useCallback((data: DealershipLoginModel) => {
    if(selectedContract?._id) {
      updateDealershipLogin({
        variables: {
          params: {
            contractId: selectedContract?._id,
            dealershipLogin: {
              login: data.login,
              password: data.password,
              extraPassword: data.extraPassword
            }
          }
        }
      })
    }
  }, [selectedContract])
  
  useEffect(() => {
    if(selectedContract?.dealershipLogin) {
      formMethods.setValue('login', selectedContract.dealershipLogin.login)
      formMethods.setValue('password', selectedContract.dealershipLogin.password)
      formMethods.setValue('extraPassword', selectedContract.dealershipLogin.extraPassword)
    }
  }, [selectedContract])

  const isEditDealershipLogin = [selectedContract?.dealershipLogin?.password, selectedContract?.dealershipLogin?.password, selectedContract?.dealershipLogin?.extraPassword].some(Boolean)

  return (
    <Dialog 
      title={isEditDealershipLogin ? 'Editar seus dados de acesso à concessionária' : 'Informar seus dados de acesso à concessionária'} 
      isVisible={isVisible} 
      onClose={() => setIsVisible(false)}
    >
      <Divider sx={{ margin: '1rem 0 .8rem 0' }} />
      <Typography sx={{ color: theme.palette.grey[600], padding: '1.2rem 0' }}>
        As informações solicitadas a seguir podem variar de acordo com a concessionária. Se precisar de ajuda, entre em contato com o atendimento da sua distribuidora de energia.
      </Typography>

      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit((data) => onSubmit(data))}>
          <Input name='login' label='Login' />
          <Typography sx={{ color: theme.palette.grey[600], fontSize: '.8rem' }}>
            Geralmente CPF/CNPJ ou endereço de e-mail
          </Typography>
          <Input type="password" sx={{ margin: '1.6rem 0' }} name='password' label='Senha' />
          <Input type="password" name='extraPassword' label='Chave extra (opcinal)' />
          <Typography sx={{ color: theme.palette.grey[600], fontSize: '.8rem' }}>
            Geralmente código de segurança, número de instalação, senha adicional ou dado pessoal
          </Typography>

          <Divider sx={{ margin: '1.6rem 0' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', justifyContent: 'flex-end' }}>
            <Button onClick={() => setIsVisible(false)} color='secondary'>Cancelar</Button>
            <Button
              endIcon={updateDealershipLoginIsLoading ? <CircularProgress size={24} color='inherit' /> : null}
              disabled={updateDealershipLoginIsLoading} 
              type="submit"
            >
              {isEditDealershipLogin ? 'Atualizar dados' : 'Informar dados'}
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Dialog>
  )
}